import React, { useState, useEffect, useRef, useMemo, useCallback, Component } from "react";
import { dirOf } from "../utils/textDir";
import { useMarketStatus } from "../utils/marketStatus";
import { createPortal } from "react-dom";
import { T, UI, FONT } from "../constants/tokens";
import { useIsMobile } from "../utils/hooks";
import { timeAgoSec } from "../utils/timeAgo";
import { dateNumeric } from "../utils/formatDate";
import { fetchMarketNews, fetchMultiCompanyNews } from "../utils/finnhubNews";
import { hebrewArticle, peekArticles, isHebrew, cutSummary, SUMMARY_MAX, RETRY_MS, TRANSLATING_MS, useHebrewNews, waitingNote } from "../utils/headline";
import HoldingBadge from "./HoldingBadge";
import ArticleReader from "./ArticleReader";
import { fetchSecFilings } from "../utils/secFilings";
import { filingForArticle } from "../utils/filingMatch";
import { portfolioMatches, isPressRelease } from "../utils/newsMatch";
import { isAdvisoryArticle, isAdvisoryHeadline } from "../constants/advisory";
import { isLogoUrl, looksLikeLogo } from "../utils/newsImage";
import Icon from "./Icon";
import Headline from "./Headline";
import LTR from "./LTR";
import { modalPanel } from "./Modal";
import { Skeleton, Loading } from "./Skeleton";
import { useTranslationWait } from "../utils/translationWait";

// ─── Error boundary — keeps a single render-time exception from freezing the
//     whole news tab. If anything below the boundary throws, we show a quiet
//     fallback inside the tab; the rest of the app stays interactive.
class NewsErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("[News] render error:", error, info); }
  render() {
    if (this.state.error) {
      const isHe = this.props.isHe;
      return (
        <div style={{ padding: "40px 16px", textAlign: "center", color: UI.text.secondary }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><Icon name="warning" size={36} color={UI.text.secondary}/></div>
          <div style={{ fontSize: 15, marginBottom: 10 }}>
            {isHe ? "אירעה תקלה זמנית בטעינת החדשות." : "Something went wrong loading the news."}
          </div>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ background: UI.accent.soft, border: `1px solid ${UI.accent.hairline}`, color: UI.accent.base, borderRadius: T.radius.md, padding: "10px 18px", minHeight: 44, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT.body }}
          >{isHe ? "נסה שוב" : "Try again"}</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const NEWS_CATS = [
  { id: "all", label: "כל החדשות",     labelEn: "All News" },
  { id: "my",  label: "נוגע לתיק שלי", labelEn: "Touches My Portfolio" },
];

// ─── Topic categories (keyword classification on the English title) ──────────
// Secondary filter that lives below the source-pill row. Zero API cost —
// pure regex on article.title. An article that matches none of the topic
// regexes is reachable only via "הכל". First match wins (rare overlap).
const NEWS_CATEGORIES = [
  { id: "all",     label: "הכל",            labelEn: "All",     re: null },
  { id: "tech",    label: "טכנולוגיה",      labelEn: "Tech",    re: /\b(?:chip|chips|semiconductor\w*|ai|artificial\s+intelligence|software|saas|cloud|gpu|nvidia|apple|microsoft|google|alphabet|meta|facebook|amazon|tesla|intel|broadcom|amd|qualcomm|tsmc|taiwan\s+semi|salesforce|oracle|openai|chatgpt|netflix|adobe)\b/i },
  { id: "macro",   label: "מאקרו וכלכלה",   labelEn: "Macro",   re: /\b(?:fed|fomc|federal\s+reserve|ecb|boj|boe|bank\s+of\s+england|central\s+bank|inflation|deflation|rate\s+(?:cut|hike|rise|increase)|interest\s+rate|cpi|ppi|gdp|jobs\s+report|job\s+openings?|jobless|payrolls?|unemployment|recession|downturn|slowdown|pmi|labou?r\s+market|economy|economic)\b/i },
  { id: "finance", label: "פיננסים ובנקים", labelEn: "Finance", re: /\b(?:bank|banks|banking|jpmorgan|jpm\b|goldman|morgan\s+stanley|citigroup|citi\b|wells\s+fargo|bofa|bank\s+of\s+america|hsbc|barclays|deutsche\s+bank|credit\s+suisse|ubs\b|hedge\s+fund|mutual\s+fund|etfs?|treasury\s+yield|treasur\w+|bonds?|yields?|credit|mortgage|lending|loans?)\b/i },
  { id: "energy",  label: "אנרגיה וסחורות", labelEn: "Energy",  re: /\b(?:oil|crude|opec|brent|wti|natural\s+gas|gas\s+prices?|energy|gasoline|refinery|refiner|exxon|chevron|shell|bp\b|petrobras|saudi\s+aramco|gold|silver|copper|lithium|tungsten|commodity|commodities)\b/i },
  { id: "crypto",  label: "קריפטו",         labelEn: "Crypto",  re: /\b(?:bitcoin|btc\b|crypto|cryptocurrenc\w+|ethereum|eth\b|stablecoin|blockchain|coinbase|binance|kraken|ftx|defi|nft\b|mining\s+rig)\b/i },
];

// "נוגע לתיק שלי" bounds. Finnhub's company-news endpoint returns up to ~250
// articles per symbol for a two-week window, and the merged list had no cap:
// a four-holding demo portfolio rendered 1,276 cards and 1,276 <img>s, and
// the translate loop then walked all of them at 200ms a step — four minutes
// of translation calls. Seven days and forty articles is a screen a
// person can actually read; the finance filter runs on the capped list, so
// the count on screen is at most 40.
const MY_NEWS_DAYS  = 7;
const MY_NEWS_LIMIT = 40;

function classifyNewsCategory(article) {
  const t = article.title || "";
  for (const c of NEWS_CATEGORIES) {
    if (c.re && c.re.test(t)) return c.id;
  }
  return null; // unclassified — only visible under "הכל"
}

// ─── Unified typography scale (in px) ─────────────────────────────────────────
// pageTitle ≥ featuredTitle ≥ modalTitle ≥ cardTitle ≥ body ≥ meta ≥ badge.
// body ≥ 14, meta ≥ 13 per design floor.
// Every value is a step from the source scale (DESIGN.md §6). This object was
// a third parallel scale — 22 · 24 · 20 · 17 · 15 · 13 — none of whose sizes
// existed in the design language. It stays as a named alias because the news
// screen genuinely has more heading tiers than TYPE offers, but it no longer
// invents its own numbers.
const FS = {
  pageTitle:        21,     // --fs-h3
  featuredTitle:    21,     // --fs-h3 · desktop top-story card
  featuredTitleSm:  18,     // --fs-h2 · mobile top-story / modal title
  modalTitle:       21,     // --fs-h3 · desktop modal
  cardTitle:        16.5,   // --fs-h3-sm · small grid cards
  body:             14.5,   // --fs-body-sm · body text, AI summary, excerpts
  meta:             12.5,   // --fs-ui-sm · dates, captions, secondary lines
  badge:            11,     // --fs-num-micro · pill labels
};

// A publisher logo standing in for a photo · utils/newsImage.js.

// ─── Finance / non-finance classifier ─────────────────────────────────────────
// Finnhub's general feed mixes sports, entertainment, and pure geopolitics into
// the market news. Drop articles that are clearly off-topic for an investing app.
//
// Decision rule:
//   1. If TITLE matches a hype/clickbait pattern (Cramer, "10 things to watch",
//      "stocks to buy now", etc.) → DROP. Speculative content has no place in
//      an investing-as-discipline app.
//   2. Else if title or excerpt contains a clear sports / entertainment marker → DROP
//   3. Else if Finnhub already tied stock symbols to the article → KEEP
//   4. Else if title or excerpt contains a finance / market keyword → KEEP
//   5. Else → DROP (no positive signal — geopolitics, crime, fluff, etc.)
//
// HYPE_RE is checked on the original English title only, not the excerpt:
// legitimate earnings stories sometimes quote phrases like "stocks to watch"
// in the body. Title-only keeps precision high.
const HYPE_RE = /\b(?:cramer|things\s+to\s+watch|stocks?\s+to\s+watch|stocks?\s+to\s+buy|stocks?\s+to\s+sell|hot\s+stocks?|best\s+stocks?|top\s+stocks?|to\s+buy\s+now|should\s+you\s+buy|stocks?\s+that\s+could)\b/i;

const NONFIN_RE = /\b(?:nfl|nba|mlb|nhl|fifa|ncaa|mls|pga|ufc|atp|wta|qb|quarterback|touchdown|halftime|playoffs?|super[\s-]?bowl|world\s+cup|world\s+series|stanley\s+cup|olympics?|olympic\s+games|medalist|home\s+run|slam\s+dunk|head\s+coach|hall\s+of\s+fame|cowboys|knicks|yankees|lakers|patriots|hollywood|oscars?|grammys?|emmys?|red\s+carpet|premiere\s+of|box\s+office|blockbuster|actress|songwriter|concert\s+tour|album\s+release|taylor\s+swift|kardashian|kanye|beyonce|rapper|movie\s+review|film\s+review)\b/i;

const FIN_RE = /\b(?:stocks?|shares?|equit\w+|nasdaq|nyse|s&p|dow\s+jones|index|indices|earnings|revenue|profit|losses?|guidance|forecast|beats?|missed?|eps|quarterly|q[1-4]\s+results|q[1-4]\s+earnings|ipo|listing|spac|merger|acquisition|acquir\w+|buyback|dividends?|valuat\w+|fed|fomc|federal\s+reserve|ecb|boj|boe|bank\s+of\s+england|central\s+bank|rate\s+(?:cut|hike|rise|increase)|interest\s+rate|basis\s+point|bps|pmi|inflation|deflation|cpi|ppi|gdp|recession|downturn|slowdown|unemployment|jobless|job\s+openings?|labou?r\s+market|payrolls?|oil|crude|opec|brent|wti|natural\s+gas|energy\s+price|fuel\s+(?:price|shock)|supply\s+(?:shock|chain)|price\s+(?:shock|pressure|surge)|gold|silver|copper|lithium|tungsten|bitcoin|btc|ethereum|eth|crypto|stablecoin|dollar|euro|yen|yuan|sterling|rand|peso|rupee|currency|forex|bank|banks|banking|hedge\s+fund|mutual\s+fund|etf|treasury|bonds?|yields?|investors?|traders?|analysts?|upgrade|downgrade|target\s+price|price\s+target|billion|trillion|ceo|cfo|layoffs?|restructuring|tariffs?|sanctions|(?:im|ex)port\w*|semiconductor\w*|chips?|automakers?|airlines?|airline\s+industry|economy|economic|lending|loans?|mortgage|stake|wall\s+st|markets?)\b|\$\d+(?:\.\d+)?[bm]?\b/i;

function isFinancialArticle(article) {
  const title = article.title || "";
  if (HYPE_RE.test(title)) return false;
  const text = `${title} ${article.excerpt || ""}`;
  if (NONFIN_RE.test(text)) return false;
  if (Array.isArray(article.symbols) && article.symbols.length > 0) return true;
  if (FIN_RE.test(text)) return true;
  return false;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// A summary on screen ends where a sentence ends (sprint 13, 1.2) · the same
// rule as the translation (utils/headline.js, cutSummary). Only when no
// sentence fits is it cut at a word, and then it says so with "…".
function clipSummary(text, max = SUMMARY_MAX) {
  if (!text) return "";
  const c = cutSummary(text, max);
  return c.length < text.trim().length && !/[.!?]$/.test(c) ? `${c}…` : c;
}

// Which of the user's symbols this article is actually about · the shared
// rule in utils/newsMatch.js: never a press release; the ticker or the
// company's name in the HEADLINE; or Finnhub's own `related` tag on the
// general feed. A mention in the body alone does not count: "touches my
// portfolio" is a claim, and a wrong one is worse than a missing badge.
// A headline worded as advice is never tied to the reader's holdings
// (sprint 13, 1.5): no "בתיק שלך" mark, not sorted first, not translated on
// its own, not in "נוגע לתיק שלי". Personal fit + the wording of an action is
// the Israel Securities Authority's test (constants/advisory.js). It stays in
// the general feed as one more article.
function matchingSymbols(article, userSymbols, names = {}) {
  if (isAdvisoryArticle(article)) return [];
  return portfolioMatches(article, userSymbols, names);
}

// The headline a card and the reading window both show. One resolver, so the
// two can never disagree: Gemini's Hebrew headline, then the quick
// translation, then the original. A card that showed English while its window
// opened in Hebrew was two resolvers reading two different states.
// The summary a card shows, in the language of its headline (sprint 13): a
// Hebrew headline takes the Hebrew summary or none; a headline still in its
// source language (not translated yet, or the translation failed) takes the
// original summary. `trans` is the translation state for the summary: null
// while it is on its way (the caller shows "מתרגם…").
function summaryFor(article, trans, { isHe, titleTranslations }) {
  if (!isHe) return article.excerpt;
  if (isHebrew(article.excerpt)) return article.excerpt;
  if (!isHebrew(headlineFor(article, { isHe, titleTranslations }))) return article.excerpt || null;
  return typeof trans === "string" && trans ? trans : (isHebrew(article.excerptHe) ? article.excerptHe : null);
}

function headlineFor(article, { isHe, titleTranslations }) {
  if (!isHe) return article.title;
  const key = article.id || article.url;
  const t = titleTranslations[key];
  // The server's Hebrew (26/10/2026) unless a translation is in state.
  if (typeof t === "string" && isHebrew(t)) return t;
  return isHebrew(article.titleHe) ? article.titleHe : article.title;
}

// ─── Article image: the article's own photo, or nothing ──────────────────────
// Rendered only when the article carries an image that is not a publisher
// logo (isLogoUrl) and that actually loads. There is no fallback picture: a
// stock photo chosen by hash — a piggy bank on a rate-decision story — is
// decoration that reads as content, and 77% of cards were showing one. No
// image means the card is text, which is what the card has.
function ArticleImg({ src, height = 220, style = {}, children, fallback = null }) {
  // Sprint 14, 2.2: an image that loads at the size of a logo (looksLikeLogo)
  // is dropped like one that failed. Until it has loaded neither it nor its
  // scrim is shown, so a logo never flashes up first. Both states are kept
  // with the address they belong to: a flag reset in an effect could land
  // after the load event of a cached image and hide it for good (review).
  const [failedSrc, setFailedSrc] = useState(null);
  const [loadedSrc, setLoadedSrc] = useState(null);
  if (!src || failedSrc === src || isLogoUrl(src)) return fallback;
  const loaded = loadedSrc === src;
  return (
    <div style={{ position: "relative", height, overflow: "hidden", flexShrink: 0, ...style }}>
      <img
        src={src}
        alt=""
        onError={() => setFailedSrc(src)}
        onLoad={e => { if (looksLikeLogo(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)) setFailedSrc(src); else setLoadedSrc(src); }}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: loaded ? 1 : 0, transition: "opacity 160ms" }}
      />
      {loaded && <div style={{ position: "absolute", inset: 0, background: "var(--si-image-scrim)" }}/>}
      {children}
    </div>
  );
}

// True when ArticleImg would render for this src — lets a caller place the
// badges over the photo when there is one and in the text flow when not.
function hasRealImage(src) {
  return !!src && !isLogoUrl(src);
}

// ─── Source badge ─────────────────────────────────────────────────────────────
// One neutral chip for every publisher. A SOURCE_BRAND table used to map eight
// publishers to colours — all eight to the same pair — so it distinguished
// nothing and read as branding the app does not do. Publishers are named,
// never styled. Renders nothing when the article carries no source name.
function SourceBadge({ source }) {
  if (!source) return null;
  return (
    <span style={{ background: UI.line.hairline, color: UI.text.secondary, borderRadius: 5, padding: "2px 8px", fontSize: FS.badge, fontWeight: 800, whiteSpace: "nowrap" }}>
      {source}
    </span>
  );
}

// ─── Article reading modal ────────────────────────────────────────────────────

function ArticleModal({
  article,
  onClose, isHe, isMobile,
  translatedTitle, translatedExcerpt,
  portfolio, watchlist, onOpenStock, pool, headlineOf, filings,
}) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Portal to document.body. On mobile this component otherwise renders
  // inside the swipeable carousel track (an ancestor with `transform: ...`
  // and `overflow: hidden`). A transformed ancestor becomes the containing
  // block for any `position: fixed` descendant — so the overlay would be
  // measured against the track instead of the viewport and clipped by the
  // carousel's overflow:hidden, leaving the page "frozen" with a hidden
  // overlay still locking body scroll.
  //
  // Mobile sizing: full-screen `inset: 0` + `100dvh`. `dvh` (dynamic
  // viewport height) is the only unit that excludes the iOS Safari URL
  // bar reliably — `vh` over-measures and pushes the bottom of the modal
  // off-screen, which is what made the modal look "invisible" on mobile.
  //
  // Backdrop click-to-close is disabled on mobile (the modal IS the
  // viewport) — the user closes via the ✕ button instead.
  // The reader carries its own scrolling body, so the panel keeps no padding
  // and its sections apply the shared gutter.
  const modalBoxStyle = isMobile
    ? modalPanel({ variant: "fullscreen", pad: false })
    : { ...modalPanel({ width: 820, pad: false }), position: "relative", overflow: "hidden" };

  return createPortal(
    <div
      onClick={isMobile ? undefined : onClose}
      style={{
        position: "fixed", inset: 0,
        background: UI.scrim,
        zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        dir={isHe ? "rtl" : "ltr"}
        onClick={e => e.stopPropagation()}
        style={modalBoxStyle}
      >
        <button
          onClick={onClose}
          aria-label={isHe ? "סגור" : "Close"}
          style={{
            position: "absolute",
            top: isMobile ? 12 : 10,
            [isHe ? "left" : "right"]: isMobile ? 12 : 10,
            background: UI.surface.overlay,
            border: `1px solid ${UI.line.hairline}`,
            borderRadius: isMobile ? 999 : 6,
            width: isMobile ? 40 : 30, height: isMobile ? 40 : 30,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: UI.text.primary,
            fontSize: isMobile ? 18 : 13,
            zIndex: 2,
            padding: 0,
          }}
        ><Icon name="x" size={isMobile ? 18 : 14} color={UI.text.primary}/></button>

        <ArticleImg src={article.image} height={isMobile ? 180 : 240}/>

        <div style={{
          padding: isMobile ? "16px 18px 24px" : "18px 22px 22px",
          overflowY: "auto", flex: 1,
          display: "flex", flexDirection: "column", gap: 12,
          // Prevent iOS rubber-band scrolling the page behind the modal
          // when the user reaches the top/bottom of the modal content.
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}>
          {/* Source + time */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <SourceBadge source={article.source}/>
            <span style={{ fontSize: FS.meta, color: UI.text.muted }}>{timeAgoSec(article.datetime)}</span>
          </div>

          {/* Title */}
          <div style={{ fontSize: isMobile ? FS.featuredTitleSm : FS.modalTitle, fontWeight: 800, color: UI.text.primary, lineHeight: 1.45 }}>
            {translatedTitle || article.title}
          </div>

          {/* Everything below the headline: the brief, the portfolio tie,
              the context, the other reports, and the way to the original. */}
          <ArticleReader
            article={article}
            pool={pool}
            portfolio={portfolio}
            watchlist={watchlist}
            excerptText={(() => {
              const ex = isHe ? (translatedExcerpt || (article.excerpt && isHebrew(article.excerpt) ? article.excerpt : null)) : article.excerpt;
              return ex ? clipSummary(ex) : null;
            })()}
            headlineOf={headlineOf}
            hebrewOnly={isHe}
            onOpenStock={onOpenStock}
            filings={filings}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────
// ─── Viewport observer ────────────────────────────────────────────────────────
// Reports once, when the wrapped card comes within 200px of the viewport, and
// then disconnects. The translate and summary loops key on these reports. A
// browser without IntersectionObserver reports immediately — the old
// behaviour, never a card that stays untranslated.
function Observe({ onVisible, children, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { onVisible(); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { onVisible(); io.disconnect(); }
    }, { rootMargin: "200px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [onVisible]);
  return <div ref={ref} style={style}>{children}</div>;
}

function NewsSkeleton() {
  const bar = (w, h = 12, mb = 0) => <Skeleton w={w} h={h} mb={mb} radius={4} style={{ flexShrink: 0 }}/>;
  return (
    <Loading style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, overflow: "hidden" }}>
          <Skeleton h={120} radius={0}/>
          <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
            {bar("40%")}{bar("90%", 14, 4)}{bar("75%", 14)}{bar("55%")}
          </div>
        </div>
      ))}
    </Loading>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
// Outer wrapper that catches any render-time error so a single broken
// article or summary can't freeze the whole tab.
// The news screen (sprint 20, stage 3: the geopolitical view moved to the
// market intelligence page, so there is one list here and no mode switch).
// A request from another screen carries a filter ("my") for the list.
function NewsTab({ request = null, onRequestHandled, ...props }) {
  const isHe = (props.lang ?? "he") !== "en";
  const [categoryRequest, setCategoryRequest] = useState(null);

  // Keyed on `request.seq`, so the same request again still applies, and
  // cleared through onRequestHandled so it fires exactly once.
  useEffect(() => {
    if (!request) return;
    if (request.category) setCategoryRequest({ category: request.category, seq: request.seq });
    onRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.seq]);

  return (
    <NewsErrorBoundary isHe={isHe}>
      <NewsTabInner {...props} categoryRequest={categoryRequest}/>
    </NewsErrorBoundary>
  );
}

function NewsTabInner({ portfolio, watchlist, lang = "he", categoryRequest = null }) {
  const market = useMarketStatus();
  const [allNews,    setAllNews]    = useState([]);
  const [myNews,     setMyNews]     = useState([]);
  const [allLoading, setAllLoading] = useState(true);
  const [myLoading,  setMyLoading]  = useState(false);
  const [category,   setCategory]   = useState("all");
  useEffect(() => { if (categoryRequest?.category) setCategory(categoryRequest.category); }, [categoryRequest?.seq]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sourceFilter,   setSourceFilter]   = useState(null); // null = all sources
  const [categoryFilter, setCategoryFilter] = useState("all"); // topic pill: tech/macro/...
  const isMobile  = useIsMobile();
  const myFetched = useRef(false);
  const isHe      = lang !== "en";

  const mySymbols    = [...new Set([...portfolio.map(s => s.symbol), ...watchlist.map(s => s.symbol)])];
  // symbol → company name, for the text side of the match. A holding whose
  // name never resolved carries its ticker, and is matched on the ticker only.
  const symbolNames  = Object.fromEntries([...portfolio, ...watchlist]
    .map(s => [s.symbol, s.name && s.name !== s.symbol ? s.name : ""]));

  // The reports to the SEC of the user's companies (sprint 21): an article
  // within two days of one shows it in its window. 16 days: the 14 kept,
  // and the two either side.
  const filingsKey = [...mySymbols].sort().join(",");
  const [filings, setFilings] = useState([]);
  useEffect(() => {
    if (!filingsKey) { setFilings([]); return undefined; }
    let alive = true;
    fetchSecFilings(filingsKey.split(","), { days: 16 }).then(r => { if (alive) setFilings(r || []); }, () => {});
    return () => { alive = false; };
  }, [filingsKey]);

  // Fetch general market news on mount
  useEffect(() => {
    let alive = true;
    setAllLoading(true);
    fetchMarketNews().then(data => {
      if (alive) { setAllNews(data); setAllLoading(false); }
    });
    return () => { alive = false; };
  }, []);

  // Fetch company news lazily when "my" tab first opened
  useEffect(() => {
    if (category !== "my" || myFetched.current) return;
    myFetched.current = true;
    if (!mySymbols.length) return;
    let alive = true;
    setMyLoading(true);
    fetchMultiCompanyNews(mySymbols, { days: MY_NEWS_DAYS, limit: MY_NEWS_LIMIT }).then(data => {
      if (alive) { setMyNews(data); setMyLoading(false); }
    });
    return () => { alive = false; };
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  const rawItems = category === "my" ? myNews   : allNews;
  const loading  = category === "my" ? myLoading : allLoading;

  // Drop sports / entertainment / pure-political articles before display.
  // useMemo so the filtered array has a stable reference per upstream fetch.
  const financeAll = useMemo(
    () => rawItems.filter(a => isFinancialArticle(a) && !(category === "my" && isAdvisoryArticle(a))),
    [rawItems, category]);
  // Hebrew mode shows no English headline (26/10/2026). Most articles come
  // with their Hebrew from the server (`titleHe`); the newest of the rest are
  // looked up again in the cache while the background run translates them,
  // and an article is listed once it has its Hebrew headline.
  const heldBack = useMemo(
    () => (isHe ? financeAll.filter(a => a.title && !isHebrew(a.title) && !isHebrew(a.titleHe)).slice(0, 40) : []),
    [financeAll, isHe]);
  const { he: lateHe } = useHebrewNews(heldBack);
  const financeItems = useMemo(() => {
    if (!isHe) return financeAll;
    return financeAll.flatMap(a => {
      if (isHebrew(a.title) || isHebrew(a.titleHe)) return [a];
      const late = lateHe[a.id || a.url];
      return late ? [{ ...a, titleHe: late.title, excerptHe: late.summary }] : [];
    });
  }, [financeAll, isHe, lateHe]);
  const waitingCount = financeAll.length - financeItems.length;

  // Available sources reflect only the finance-only feed.
  const availableSources = useMemo(
    () => [...new Set(financeItems.map(a => a.source).filter(Boolean))].sort(),
    [financeItems]
  );

  // Drop the source filter if the active feed no longer contains it
  useEffect(() => {
    if (sourceFilter && !availableSources.includes(sourceFilter)) setSourceFilter(null);
  }, [availableSources, sourceFilter]);

  // Apply source + topic filters, then sort: direct symbol match first, then by date
  let filteredRaw = financeItems;
  if (sourceFilter)              filteredRaw = filteredRaw.filter(a => a.source === sourceFilter);
  if (categoryFilter !== "all")  filteredRaw = filteredRaw.filter(a => classifyNewsCategory(a) === categoryFilter);
  // Matched once per article, not once per comparison.
  const hits = new Map(filteredRaw.map(a => [a, matchingSymbols(a, mySymbols, symbolNames).length ? 1 : 0]));
  const items = [...filteredRaw].sort((a, b) => {
    const aHit = hits.get(a);
    const bHit = hits.get(b);
    if (bHit !== aHit) return bHit - aHit;
    return b.datetime - a.datetime;
  });

  // A hero card only when the top article has a photo of its own. Without one
  // the "hero" was a text box a screen wide above a grid of photo cards — and
  // its label promised an editorial pick that does not exist: this is the
  // newest article (or the first naming a holding), not the most important.
  // No photo → no hero, no label; the grid starts at the first article.
  // A press release is never the lead: it is an issuer's advertisement, and
  // the lead slot is the one place a reader assumes an editor chose.
  // Nor is a headline worded as advice ("Should investors buy…?",
  // constants/advisory.js): the lead is the first article that is neither.
  //
  // Sprint 13, 1.4: the lead is, first, an article from the last 24 hours that
  // names a holding and has a photo of its own. Only when there is none does
  // the rule above apply (the first eligible article, if it has a photo).
  const eligible = items.filter(a => !isPressRelease(a) && !isAdvisoryArticle(a));
  const dayAgo   = Date.now() / 1000 - 24 * 3600;
  const lead     = eligible.find(a => (a.datetime || 0) >= dayAgo && hits.get(a) && hasRealImage(a.image))
                || eligible[0];
  const featured = lead && hasRealImage(lead.image) ? lead : null;
  const rest     = featured ? items.filter(a => a !== featured) : items;
  // Which cards have a report of the company to the SEC (sprint 21) · once
  // per list and reports, not on every render.
  const filingOf = useMemo(() => {
    const m = new Map();
    if (filings.length) for (const a of items) {
      const f = filingForArticle(a, matchingSymbols(a, mySymbols, symbolNames), filings);
      if (f) m.set(a, f);
    }
    return m;
  }, [items, filings]); // eslint-disable-line react-hooks/exhaustive-deps

  // "LIVE" must reflect real freshness, not just be a permanent decoration —
  // true only when the newest article in the current feed actually arrived
  // recently. Otherwise show its real relative age instead.
  const newestTs   = financeItems.reduce((max, a) => Math.max(max, a.datetime || 0), 0);
  // And never while the US market is closed (2026-09-26).
  const isLiveFeed = market.open && newestTs > 0 && (Date.now() / 1000 - newestTs) < 3600;

  const [selectedArticle, setSelectedArticle] = useState(null);

  // Keys of the cards that have scrolled into (or near) the viewport. Hebrew
  // translation and the Gemini summary are fetched for these and only these:
  // a card the reader never reaches costs nothing. The featured story is
  // added on mount, since it is the first thing on screen either way.
  const [visibleKeys, setVisibleKeys] = useState(() => new Set());
  const markVisible = useCallback((key) => {
    setVisibleKeys(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);
  // A stable string for effect deps — a Set is a new object on every change.
  const visibleKey = useMemo(() => [...visibleKeys].sort().join("\u0001"), [visibleKeys]);

  const [translations,    setTranslations]    = useState({});
  const translatingRef = useRef(new Set());
  // autoOf is defined below translateArticle; the ref lets the callback read it.
  const autoRef = useRef(() => false);
  // The "תרגם" comeback timers, cleared when the screen goes away.
  const retryTimers = useRef(new Set());
  useEffect(() => () => { retryTimers.current.forEach(clearTimeout); retryTimers.current.clear(); }, []);
  const [titleTranslations, setTitleTranslations] = useState({});
  const titleTranslatingRef = useRef(new Set());
  // The same rule for the Hebrew headline, once it has arrived.
  const heAdvisory = a => {
    const tr = titleTranslations[a.id || a.url];
    return typeof tr === "string" && isAdvisoryHeadline(tr);
  };


  // The featured story is on screen from the first paint: mark it visible so
  // the translate loop covers it without waiting for an observer.
  useEffect(() => {
    if (featured) markVisible(featured.id || featured.url);
  }, [featured, markVisible]);

  // Article open/close always lands at the top of the page. The window no
  // longer shows the per-article summary (its "בקצרה" is built per event,
  // ArticleReader), so opening an article no longer requests one.
  useEffect(() => { window.scrollTo(0, 0); }, [selectedArticle]);

  // Everything already fetched on this screen · the pool the reader searches
  // for other reports of the same event.
  const clusterPool = useMemo(() => [...allNews, ...myNews], [allNews, myNews]);

  // Translate the cards on screen: headline and summary together, in one
  // request to the shared Gemini batch (utils/headline.js, hebrewArticle).
  // Visible cards only (visibleKeys). The refs dedup across passes, so a card
  // is translated once.
  //
  // `cancelled` stops this pass from STARTING more work when the effect
  // re-runs (a new card scrolled into view) — it never discards a result that
  // comes back. The first version gated the setters on it too, and since the
  // effect now re-runs on every newly visible card, the cleanup fired while
  // the first translations were in flight: their results were dropped, the
  // state stayed `null`, "מתרגם…" stayed on screen for good, and the dedup ref
  // blocked a retry. A result that arrives is always recorded; a failure
  // records the original text (the Headline note says why it is English).
  //
  // After a failure (budget spent, Gemini down) a card with a "תרגם" button
  // gets it back after RETRY_MS, with its original headline, the note and its
  // original summary meanwhile (sprint 13). A holding's card, translated on its
  // own, is not retried automatically · a reload retries it.
  // One article, both halves, one request: the headline and the summary go
  // to Gemini in the same batch (cached for everyone). The second, keyless
  // translator is gone (DECISIONS.md, 28/09/2026). A summary with no Hebrew translation is
  // recorded as "" and NOT shown · an English summary under a Hebrew headline
  // is not shown in Hebrew mode.
  const translateArticle = useCallback((article) => {
    const key = article.id || article.url;
    if (titleTranslatingRef.current.has(key) || !article.title) return;
    titleTranslatingRef.current.add(key);
    translatingRef.current.add(key);
    // The summary only · with none, nothing (a copy of the headline is not one).
    const hasSummary = !!(article.excerpt && article.excerpt.trim());
    setTitleTranslations(prev => ({ ...prev, [key]: null }));
    if (hasSummary) setTranslations(prev => ({ ...prev, [key]: null }));
    hebrewArticle(article).then(({ title, summary }) => {
      setTitleTranslations(prev => ({ ...prev, [key]: title || article.title }));
      if (hasSummary) setTranslations(prev => ({ ...prev, [key]: summary || "" }));
      // The translation failed (daily budget spent, Gemini down): the card
      // stays in the source language with its note, and "תרגם" comes back
      // after RETRY_MS (sprint 13). A holding's card is translated on its own
      // and has no button, so it is not reset here and not retried in a loop.
      // hebrewArticle answers with the original headline when there is no
      // translation, never with null · so "failed" is "still not Hebrew".
      if (!isHebrew(title || article.title) && !autoRef.current(article)) {
        const timer = setTimeout(() => {
          retryTimers.current.delete(timer);
          titleTranslatingRef.current.delete(key);
          translatingRef.current.delete(key);
          setTitleTranslations(prev => { const n = { ...prev }; delete n[key]; return n; });
          setTranslations(prev => { const n = { ...prev }; delete n[key]; return n; });
        }, RETRY_MS);
        retryTimers.current.add(timer);
      }
    });
  }, []);

  // Automatic translation, for the cards on screen that are about a HOLDING
  // (DECISIONS.md, 2026-09-26). Everything else stays in the source language
  // with a "תרגם" button; a translation someone asked for is cached on the
  // server for every user. The headlines of one pass go out as one batch.
  const heldSymbols = useMemo(() => portfolio.map(p => p.symbol), [portfolio]);
  const autoOf = useCallback(a => matchingSymbols(a, heldSymbols, symbolNames).length > 0,
    // symbolNames is rebuilt each render from the same lists
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [heldSymbols.join(",")]);
  autoRef.current = autoOf;
  useEffect(() => {
    if (!isHe || !financeItems.length || !visibleKeys.size) return;
    for (const article of financeItems) {
      if (visibleKeys.has(article.id || article.url) && autoOf(article)) translateArticle(article);
    }
  }, [financeItems, isHe, visibleKey, autoOf, translateArticle]); // eslint-disable-line react-hooks/exhaustive-deps

  // Everything else on screen: the server translates the feed every hour
  // (news-translate-scheduled.js), so most cards already have Hebrew in the
  // shared cache. One cache lookup per pass · no Gemini call · fills them in.
  // What the cache does not have · an article newer than the last hourly run
  // · is translated now, in the same shared batch, and cached for everyone
  // (20/10/2026: the quota is 450 a day, not 20). "תרגם" is left only on a
  // card whose translation failed, after RETRY_MS (translateArticle).
  const peekedRef = useRef(new Set());
  // Cards whose cache lookup has answered, hit or miss · until then a card
  // that may get its Hebrew from the cache is still waiting for it.
  const [peekDone, setPeekDone] = useState(() => new Set());
  useEffect(() => {
    if (!isHe || !financeItems.length || !visibleKeys.size) return;
    const ask = financeItems.filter(a => {
      const key = a.id || a.url;
      return visibleKeys.has(key) && !autoOf(a) && !peekedRef.current.has(key)
        && !titleTranslatingRef.current.has(key) && a.title && !isHebrew(a.title);
    });
    if (!ask.length) return;
    ask.forEach(a => peekedRef.current.add(a.id || a.url));
    peekArticles(ask).then(found => {
      setPeekDone(prev => { const n = new Set(prev); ask.forEach(a => n.add(a.id || a.url)); return n; });
      const titles = {}, sums = {}, misses = [];
      for (const a of ask) {
        const key = a.id || a.url;
        if (titleTranslatingRef.current.has(key)) continue;
        const hit = found[String(a.id || a.url || a.title).slice(0, 200)];
        // Only a complete hit is used: a Hebrew headline over a summary that
        // is not translated would hide the summary and the "תרגם" button.
        const wantSum = !!(a.excerpt && a.excerpt.trim() && !isHebrew(a.excerpt));
        if (!hit || (wantSum && !hit.summary)) { misses.push(a); continue; }
        titles[key] = hit.title;
        if (wantSum) sums[key] = hit.summary;
      }
      if (Object.keys(titles).length) setTitleTranslations(prev => ({ ...titles, ...prev }));
      if (Object.keys(sums).length) setTranslations(prev => ({ ...sums, ...prev }));
      misses.forEach(translateArticle);
    });
  }, [financeItems, isHe, visibleKey, autoOf, translateArticle]); // eslint-disable-line react-hooks/exhaustive-deps

  // A headline whose Hebrew is on its way · a holding's card being translated,
  // or any card whose cache lookup has not answered · waits under a
  // placeholder instead of flashing the English, up to 6 seconds from when
  // the card came on screen (utils/translationWait.js). Its summary and
  // "תרגם" wait with it.
  const waiting = useTranslationWait([...visibleKeys]);
  const pendingOf = a => {
    if (!isHe || !a?.title || isHebrew(a.title) || isHebrew(a.titleHe)) return false;
    const key = a.id || a.url;
    if (typeof titleTranslations[key] === "string") return false;
    if (!autoOf(a) && peekDone.has(key)) return false;
    return waiting(key);
  };


  return (
    <div dir={isHe ? "rtl" : "ltr"} style={{ display: "flex", flexDirection: "column", gap: 0, background: UI.surface.page }}>

      {/* ── Article modal ── */}
      {selectedArticle && (() => {
        const key          = selectedArticle.id || selectedArticle.url;
        // Gemini's Hebrew headline and summary. In Hebrew mode an English
        // summary is never shown: a Hebrew one, or none.
        const translatedTitle   = isHe ? headlineFor(selectedArticle, { isHe, titleTranslations }) : null;
        const translatedExcerpt = isHe ? (typeof translations[key] === "string" && translations[key] ? translations[key] : (selectedArticle.excerptHe || null)) : null;
        return (
          <ArticleModal
            article={selectedArticle}
            onClose={() => setSelectedArticle(null)}
            // The reader sits above the company page, so it closes first.
            onOpenStock={sym => {
              setSelectedArticle(null);
              window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol: sym } }));
            }}
            isHe={isHe}
            isMobile={isMobile}
            translatedTitle={translatedTitle}
            translatedExcerpt={translatedExcerpt}
            portfolio={portfolio}
            watchlist={watchlist}
            // Everything already loaded on this screen: the pool the reader
            // looks for other reports of the same event in.
            pool={clusterPool}
            headlineOf={a => headlineFor(a, { isHe, titleTranslations })}
            filings={filings}
          />
        );
      })()}

      {/* ── Masthead ──
          No title of its own: the news/geo switch above this component names
          the screen, and an h1 reading "חדשות" 80px under a button reading
          "חדשות" said it twice. The date and the feed's freshness sit on the
          category-tabs row instead, at its far end. */}
      <div style={{ padding: "6px 0 10px", borderBottom: `2px solid ${UI.text.primary}` }}>
        {/* Category tabs · date · freshness — one row; the date drops under
            the tabs on a narrow phone rather than squeezing them. */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: isMobile ? 6 : 0, overflowX: "auto", scrollbarWidth: "none", paddingBottom: isMobile ? 2 : 0 }}>
          {NEWS_CATS.map(cat => {
            const label  = isHe ? cat.label : cat.labelEn;
            const active = category === cat.id;
            return isMobile ? (
              /* The selected plate, the same one every other filter on this
                 screen wears. This pair used to invert instead — ink block,
                 page-coloured text — which made it the hardest thing on the
                 news screen and a different answer to the same question. */
              <button key={cat.id} onClick={() => setCategory(cat.id)} style={{
                background: active ? UI.state.surface : "transparent",
                border: `1px solid ${active ? UI.state.surface : UI.line.hairline}`,
                borderRadius: 999, padding: "8px 16px", minHeight: 40, fontSize: 14,
                fontWeight: active ? 800 : 600, color: active ? UI.state.fg : UI.text.muted,
                cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                fontFamily: FONT.body,
              }}>{label}</button>
            ) : (
              <button key={cat.id} onClick={() => setCategory(cat.id)} style={{
                background: "none", border: "none",
                borderBottom: active ? `3px solid ${UI.text.primary}` : "3px solid transparent",
                padding: "8px 16px", fontSize: 14, fontWeight: active ? 800 : 600,
                color: active ? UI.text.primary : UI.text.muted,
                cursor: "pointer", whiteSpace: "nowrap", marginBottom: -2,
                fontFamily: FONT.body,
              }}>{label}</button>
            );
          })}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, fontSize: FS.meta, color: UI.text.muted }}>
          <span style={{ whiteSpace: "nowrap" }}>{dateNumeric(new Date(), { lang: isHe ? "he" : "en", weekday: true })}</span>
          {(isLiveFeed || timeAgoSec(newestTs)) && (<>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: isLiveFeed ? UI.state.line : UI.text.muted, display: "inline-block" }}/>
            <span style={{ fontSize: FS.badge, fontWeight: 800, color: isLiveFeed ? UI.text.secondary : UI.text.muted, whiteSpace: "nowrap" }}>
              {isLiveFeed ? "LIVE" : timeAgoSec(newestTs)}
            </span>
          </>)}
        </div>
        </div>

        {/* Source filter pills */}
        {availableSources.length > 1 && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none", marginTop: 10, paddingBottom: 2 }}>
            {[null, ...availableSources].map(src => {
              const active = sourceFilter === src;
              const label  = src ?? (isHe ? "כל המקורות" : "All sources");
              return (
                <button
                  key={src ?? "__all__"}
                  onClick={() => setSourceFilter(src)}
                  // Same selected plate as every other filter in the app —
                  // the source's own brand tint was the last place a selected
                  // control wore something other than --selected-bg/-fg.
                  style={{
                    background: active ? UI.state.surface : "transparent",
                    border: `1px solid ${active ? UI.state.surface : UI.line.hairline}`,
                    borderRadius: 999, padding: "6px 14px", fontSize: FS.meta,
                    fontWeight: active ? 800 : 600,
                    color: active ? UI.state.fg : UI.text.muted,
                    cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                    fontFamily: FONT.body,
                  }}
                >{label}</button>
              );
            })}
          </div>
        )}

        {/* Topic category pills — keyword classification on English title, zero API cost */}
        <div style={{ display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none", marginTop: 8, paddingBottom: 2 }}>
          {NEWS_CATEGORIES.map(cat => {
            const active = categoryFilter === cat.id;
            const label  = isHe ? cat.label : cat.labelEn;
            return (
              <button
                key={cat.id}
                onClick={() => setCategoryFilter(cat.id)}
                style={{
                  background: active ? UI.state.surface : "transparent",
                  border: `1px solid ${active ? UI.state.surface : UI.line.hairline}`,
                  borderRadius: 999, padding: "6px 14px", fontSize: FS.meta,
                  fontWeight: active ? 800 : 600,
                  color: active ? UI.state.fg : UI.text.muted,
                  cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                  fontFamily: FONT.body,
                }}
              >{label}</button>
            );
          })}
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && <div style={{ marginTop: 18 }}><NewsSkeleton/></div>}

      {/* ── Empty ── */}
      {!loading && items.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: UI.text.muted }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><Icon name="inbox" size={36} color={UI.text.muted}/></div>
          <div style={{ fontSize: 15 }}>
            {category === "my" && !mySymbols.length
              ? (isHe ? "הוסף מניות לתיק כדי לראות חדשות רלוונטיות" : "Add stocks to see relevant news")
              : waitingNote(waitingCount) || (isHe ? "אין חדשות זמינות כרגע" : "No news available right now")}
          </div>
        </div>
      )}

      {/* Articles held back until their Hebrew headline is in (26/10/2026). */}
      {!loading && items.length > 0 && waitingNote(waitingCount) && (
        <div role="status" style={{ fontSize: 12.5, color: UI.text.muted, marginBottom: 12 }}>{waitingNote(waitingCount)}</div>
      )}

      {/* ── Featured story ── */}
      {!loading && featured && (() => {
        const featKey     = featured.id || featured.url;
        const featMatches = heAdvisory(featured) ? [] : matchingSymbols(featured, mySymbols, symbolNames);
        return (
          <div style={{ marginTop: 18, marginBottom: 20 }}>
            <div
              onClick={() => setSelectedArticle(featured)}
              style={{ borderRadius: 6, overflow: "hidden", border: `1px solid ${UI.line.hairline}`, boxShadow: UI.shadow.card, cursor: "pointer" }}
            >
              {(() => {
                // The hero exists only with a photo (see `featured`), so the
                // badge row sits over it. When the photo turns out to be a
                // logo once it loads (sprint 14, 2.2), the same row moves into
                // the text, above the time.
                const badges = over => (
                  <div style={over
                    ? { position: "absolute", bottom: 14, right: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }
                    : { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", background: UI.surface.raised, padding: isMobile ? "16px 16px 0" : "22px 26px 0" }}>
                    {/* A category label, never a number that fell — so it takes no reserved
                        hue. It wore UI.loss, which VISION reserves for a value going
                        down; on the light theme a red chip on a photo made the point
                        unmissable. Solid surface + hairline reads on any photo. */}
                    <span style={{ background: UI.surface.overlay, color: UI.text.primary, border: `1px solid ${UI.line.strong}`, borderRadius: 5, padding: "4px 12px", fontSize: FS.badge, fontWeight: 800 }}>הידיעה הראשית</span>
                    <SourceBadge source={featured.source}/>
                    {featMatches.slice(0, 3).map(sym => (
                      <HoldingBadge key={sym} symbol={sym} withSymbol portfolio={portfolio} watchlist={watchlist} isHe={isHe}/>
                    ))}
                    {featMatches.length > 3 && (
                      <span style={{ fontSize: FS.badge, fontWeight: 800, color: UI.text.secondary }}>
                        +{featMatches.length - 3}
                      </span>
                    )}
                  </div>
                );
                return (
                  <>
                    <ArticleImg src={featured.image} height={isMobile ? 200 : 280} fallback={badges(false)}>{badges(true)}</ArticleImg>
                    <div style={{ background: UI.surface.raised, padding: isMobile ? "16px 16px 18px" : "22px 26px 24px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                        <span style={{ fontSize: FS.meta, color: UI.text.muted }}>{timeAgoSec(featured.datetime)}</span>
                      </div>
                {pendingOf(featured) ? (
                  <div aria-busy="true" style={{ marginBottom: 16 }}>
                    <Skeleton h={isMobile ? 22 : 28} w="94%" mb={8} label="מתרגם" ms={TRANSLATING_MS}/>
                    <Skeleton h={isMobile ? 22 : 28} w="62%" ms={TRANSLATING_MS}/>
                  </div>
                ) : <Headline
                  text={headlineFor(featured, { isHe, titleTranslations })}
                  source={!isHe}
                  style={{ fontSize: isMobile ? FS.featuredTitleSm : FS.featuredTitle, fontWeight: 800, color: UI.text.primary, lineHeight: 1.4, marginBottom: 14 }}
                />}
                {!pendingOf(featured) && (() => {
                  const fTrans = translations[featKey];
                  if (isHe && fTrans === null) return <div style={{ fontSize: FS.meta, color: UI.text.muted, marginBottom: 16 }}>מתרגם...</div>;
                  // One language per card: the summary follows the headline
                  // (summaryFor). An untranslated headline, or one whose
                  // translation failed, keeps its original summary.
                  const excerpt = summaryFor(featured, fTrans, { isHe, titleTranslations });
                  return excerpt ? (
                    <div dir={dirOf(excerpt)} style={{ fontSize: FS.body, color: UI.text.secondary, lineHeight: 1.8, marginBottom: 16 }}>
                      {clipSummary(excerpt)}
                    </div>
                  ) : null;
                })()}
                {featured.symbols.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {featured.symbols.map(sym => {
                      const hit = featMatches.includes(sym);
                      return (
                        <span key={sym} style={{ background: hit ? UI.line.strong : UI.fill.subtle, color: hit ? UI.text.primary : UI.text.secondary, border: `1px solid `, borderRadius: 6, padding: "4px 12px", fontSize: FS.meta, fontWeight: 700 }}>{sym}</span>
                      );
                    })}
                  </div>
                )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* ── The articles (all of them when there is no hero) ── */}
      {!loading && rest.length > 0 && (
        <>
          {featured && (
            <div style={{ fontSize: FS.badge, fontWeight: 800, color: UI.text.muted, textTransform: "uppercase", marginBottom: 10, borderTop: `1px solid ${UI.line.hairline}`, paddingTop: 14 }}>
              {isHe ? "עוד כתבות" : "More Stories"}
            </div>
          )}
          {/* Each card is as tall as what it holds (sprint 13, 1.3): a card
              with no photo is source, time, headline and summary, with no
              empty block where a photo would have been. alignItems "start"
              keeps a short card from being stretched to its neighbour. */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginTop: featured ? 0 : 18, marginBottom: 20, alignItems: "start" }}>
            {rest.map(n => {
              const nKey    = n.id || n.url;
              const matches = heAdvisory(n) ? [] : matchingSymbols(n, mySymbols, symbolNames);
              return (
                <Observe key={nKey} onVisible={() => markVisible(nKey)} style={{ display: "flex" }}>
                <div
                  onClick={() => setSelectedArticle(n)}
                  className="si-card si-card-click"
                  style={{ flex: 1, background: UI.surface.card, borderRadius: T.radius.xl, overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer" }}
                >
                  <ArticleImg src={n.image} height={120}/>
                  <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                      {/* The portfolio mark comes FIRST: on this screen the
                          question a reader is scanning for is "does this touch
                          me", and the answer was arriving third, at 11px,
                          after the source. */}
                      {matches.slice(0, 3).map(sym => (
                        <HoldingBadge key={sym} symbol={sym} withSymbol portfolio={portfolio} watchlist={watchlist} isHe={isHe}/>
                      ))}
                      <SourceBadge source={n.source}/>
                      {/* The company reported on it to the SEC (sprint 21): the
                          report is in the article's window. */}
                      {filingOf.has(n) && (
                        <span data-news-filing style={{ fontSize: FS.badge, fontWeight: 700, color: UI.text.secondary, border: `1px solid ${UI.line.strong}`, borderRadius: 5, padding: "1px 7px" }}>
                          דיווח ל-<LTR>SEC</LTR>
                        </span>
                      )}
                      {matches.length > 3 && (
                        <span style={{ fontSize: FS.badge, fontWeight: 800, color: UI.text.muted }}>
                          +{matches.length - 3}
                        </span>
                      )}
                      <span style={{ fontSize: FS.meta, color: UI.text.muted, marginInlineStart: "auto" }}>{timeAgoSec(n.datetime)}</span>
                    </div>
                    {/* The same note as the lead when a translation was asked for and
                        did not come (sprint 13): the reader sees why it is English. */}
                    {pendingOf(n) ? (
                      <div aria-busy="true" style={{ marginBottom: 8 }}>
                        <Skeleton h={18} w="92%" mb={6} label="מתרגם" ms={TRANSLATING_MS}/>
                        <Skeleton h={18} w="58%" ms={TRANSLATING_MS}/>
                      </div>
                    ) : <Headline
                      text={headlineFor(n, { isHe, titleTranslations })}
                      source={!isHe}
                      style={{ fontSize: FS.cardTitle, fontWeight: 800, color: UI.text.primary, lineHeight: 1.5, marginBottom: 8, textAlign: "start" }}
                    />}
                    {!pendingOf(n) && (() => {
                      const nTrans = translations[nKey];
                      if (isHe && nTrans === null) return <div style={{ fontSize: FS.meta, color: UI.text.muted, marginBottom: 8 }}>מתרגם...</div>;
                      const excerpt = summaryFor(n, nTrans, { isHe, titleTranslations });
                      return excerpt ? (
                        <div dir={dirOf(excerpt)} style={{ fontSize: 14, color: UI.text.secondary, lineHeight: 1.65, marginBottom: 8 }}>
                          {clipSummary(excerpt, 220)}
                        </div>
                      ) : null;
                    })()}
                    {n.symbols.length > 0 && (
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {n.symbols.map(sym => {
                          const hit = matches.includes(sym);
                          return (
                            <span key={sym} style={{ background: hit ? UI.line.strong : UI.fill.subtle, color: hit ? UI.text.primary : UI.text.secondary, borderRadius: 5, padding: "2px 8px", fontSize: FS.badge, fontWeight: 700 }}>{sym}</span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                </Observe>
              );
            })}
          </div>
        </>
      )}

    </div>
  );
}

export default NewsTab;
